import { VENDOR_NAME_KEY } from '~/theme/vendorConstants';

export interface VendorTheme {
  /** Primary brand color — replaces --cobuDarkBlue and its derived rgba variants. */
  darkBlue: string;
  /** Font family for headings, breadcrumbs, and body text. */
  fontFamily: string;
  /** Text color for page headings (h1–h4, StatsBox titles). */
  pageHeaderColor: string;
  /** Left bracket/strip color for StatsBox. Empty string = use per-box prop colors. */
  bracketColor: string;
  /** Background for number bubbles, chips, card backgrounds. Empty string = use default rgba vars. */
  cardBackground: string;
  /** Body/description text color. */
  textColor: string;
  /** Active tab/button background color. */
  tabColor: string;
  /** Primary action button background color. */
  buttonPrimaryColor: string;
  /** Primary action button text color. */
  buttonPrimaryTextColor: string;
  /** H1 title color. Empty string = use --cobuDarkBlue fallback. */
  h1Color: string;
  /** Comment body text color. Empty string = use --cobuDarkBlue fallback. */
  commentTextColor: string;
  /** Reply button text color. Empty string = use --cobuRed fallback. */
  replyButtonColor: string;
  /** Teal accent for Users stats column and events border. Empty string = use per-column default. */
  tealAccent: string;
  /** Pink accent for Building Activity stats column. Empty string = use per-column default. */
  pinkAccent: string;
  /** Building section accent color. Empty string = use cobuPurple default. */
  buildingAccent: string;
  /** Page background color (behind content panels). Empty string or absent = transparent. */
  pageBackground?: string;
  /** Border color for stat boxes and number bubbles. Empty string = use rgba fallback. */
  statBorderColor: string;
}

export const COBU_THEME: VendorTheme = {
  darkBlue: '#272163', // rgb(39, 33, 99)
  fontFamily: 'Roboto',
  pageHeaderColor: '#272163',
  bracketColor: '',
  cardBackground: '',
  textColor: '', // empty = use CSS fallback (--cobuLightGreyPointFive)
  tabColor: '#272163',
  buttonPrimaryColor: '#fec100',
  buttonPrimaryTextColor: '#272163',
  h1Color: '',
  commentTextColor: '',
  replyButtonColor: '',
  tealAccent: '',
  pinkAccent: '',
  buildingAccent: '',
  pageBackground: '',
  statBorderColor: ''
};

const VENDOR_THEMES: Record<string, VendorTheme> = {
  homebody: {
    darkBlue: '#004B37', // rgb(0, 75, 55)
    fontFamily: 'Inter',
    pageHeaderColor: '#1A1A1A',
    bracketColor: '',
    cardBackground: '#FFFFFF',
    textColor: '#98A2B3',
    tabColor: '#000000',
    buttonPrimaryColor: '#000000',
    buttonPrimaryTextColor: '#ffffff',
    h1Color: '#1A1A1A',
    commentTextColor: '#1A1A1A',
    replyButtonColor: '#2d7ff9',
    tealAccent: '#0E918C',
    pinkAccent: '#C4007A',
    buildingAccent: '#1A1A1A',
    statBorderColor: '#E5E7EB'
  }
};

const hexToRgb = (hex: string) => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16)
];

/**
 * Reads the stored vendor name and applies its theme to CSS custom properties.
 * Should be called once on app startup.
 */
export const applyVendorTheme = () => {
  const vendor = sessionStorage.getItem(VENDOR_NAME_KEY);
  const theme = (vendor && VENDOR_THEMES[vendor]) || COBU_THEME;
  const [r, g, b] = hexToRgb(theme.darkBlue);
  const root = document.documentElement;

  root.style.setProperty('--cobuDarkBlue', theme.darkBlue);
  root.style.setProperty('--vendorFontFamily', theme.fontFamily);
  root.style.setProperty('--vendorPageHeaderColor', theme.pageHeaderColor);
  if (theme.textColor) {
    root.style.setProperty('--vendorTextColor', theme.textColor);
  } else {
    root.style.removeProperty('--vendorTextColor');
  }
  root.style.setProperty('--vendorTabColor', theme.tabColor);
  root.style.setProperty('--vendorButtonPrimaryColor', theme.buttonPrimaryColor);
  root.style.setProperty('--vendorButtonPrimaryTextColor', theme.buttonPrimaryTextColor);

  if (theme.h1Color) {
    root.style.setProperty('--vendorH1Color', theme.h1Color);
  } else {
    root.style.removeProperty('--vendorH1Color');
  }

  if (theme.commentTextColor) {
    root.style.setProperty('--vendorCommentTextColor', theme.commentTextColor);
  } else {
    root.style.removeProperty('--vendorCommentTextColor');
  }

  if (theme.replyButtonColor) {
    root.style.setProperty('--vendorReplyButtonColor', theme.replyButtonColor);
  } else {
    root.style.removeProperty('--vendorReplyButtonColor');
  }
  root.style.setProperty('--cobuLightGreyPointZeroThree', `rgba(${r}, ${g}, ${b}, 0.03)`);
  root.style.setProperty('--cobuLightGreyPointZeroFive', `rgba(${r}, ${g}, ${b}, 0.05)`);
  root.style.setProperty('--cobuLightGreyPointOne', `rgba(${r}, ${g}, ${b}, 0.1)`);
  root.style.setProperty('--cobuLightGreyPointTwo', `rgba(${r}, ${g}, ${b}, 0.2)`);
  root.style.setProperty('--cobuLightGreyPointThree', `rgba(${r}, ${g}, ${b}, 0.3)`);
  root.style.setProperty('--cobuLightGreyPointFive', `rgba(${r}, ${g}, ${b}, 0.5)`);

  if (theme.bracketColor) {
    root.style.setProperty('--vendorBracketColor', theme.bracketColor);
  } else {
    root.style.removeProperty('--vendorBracketColor');
  }

  if (theme.cardBackground) {
    root.style.setProperty('--vendorCardBackground', theme.cardBackground);
  } else {
    root.style.removeProperty('--vendorCardBackground');
  }

  if (theme.tealAccent) {
    root.style.setProperty('--vendorTealAccent', theme.tealAccent);
  } else {
    root.style.removeProperty('--vendorTealAccent');
  }

  if (theme.pinkAccent) {
    root.style.setProperty('--vendorPinkAccent', theme.pinkAccent);
    root.style.setProperty('--cobuRed', theme.pinkAccent);
  } else {
    root.style.removeProperty('--vendorPinkAccent');
  }

  if (theme.pageBackground) {
    root.style.setProperty('--vendorPageBackground', theme.pageBackground);
  } else {
    root.style.removeProperty('--vendorPageBackground');
  }

  if (theme.statBorderColor) {
    root.style.setProperty('--vendorStatBorderColor', theme.statBorderColor);
  } else {
    root.style.removeProperty('--vendorStatBorderColor');
  }
};

/** Returns the active theme object without mutating the DOM. */
export const getActiveTheme = (): VendorTheme => {
  const vendor = sessionStorage.getItem(VENDOR_NAME_KEY);
  return (vendor && VENDOR_THEMES[vendor]) || COBU_THEME;
};
